import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PaginatedTableColumn, PaginatedTableData } from '@common/components';
import { PATHS } from '@common/constants';
import { useTable } from '@common/hooks';
import { getDateWithoutTZ, maskPhoneNumber } from '@common/utils';
import { useQueryReferrals } from '@modules/genetic-counseling/hooks';
import { GENETIC_COUNSELING_TRANSLATION, PAGE_SIZE } from '../constants';
import { ReferralListItem, ReferralsParams, ReferralStatuses, UseReferralsTableValue } from '../types';
import { BasicFilters } from '../components/Automated/shared/BasicFilter/BasicFilter.types';

const useReferralsTable = <T extends ReferralListItem>(referralStatus: ReferralStatuses): UseReferralsTableValue<T> => {
  const navigate = useNavigate();
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const pageSize = PAGE_SIZE;
  const [dataTable, setDataTable] = useState<PaginatedTableData<T>>();
  const { handleChangePage, handleChangeSorting, params, offset, setParams, setOffset } = useTable<ReferralsParams>({
    limit: pageSize,
    status: referralStatus,
  });
  const { data, isLoading, isFetching, isError } = useQueryReferrals<T>(params, {
    enabled: true,
  });

  const handleFilterChange = ({ query, dateRange }: BasicFilters): void => {
    setParams({
      ...params,
      q: query,
      from: dateRange?.startDate,
      to: dateRange?.endDate,
      offset: 0,
      selectedDateRange: dateRange?.selectedDateRange,
    });
    setOffset(0);
  };

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>, item: T, _index: number): void => {
    navigate(`/${PATHS.GENETIC_COUNSELING.ROOT}/${PATHS.GENETIC_COUNSELING.PATIENT}/${item.appointmentID}`);
  };

  const commonColumns: Array<PaginatedTableColumn<T>> = [
    {
      key: 'firstName',
      label: t('table.first_name'),
      sort: true,
      width: '16%',
    },
    {
      key: 'lastName',
      label: t('table.last_name'),
      sort: true,
      width: '16%',
    },
    {
      key: 'birthDate',
      label: t('table.birth_date'),
      sort: false,
      width: '16%',
      render: (_column: PaginatedTableColumn<T>, item: T) => {
        return item.dateOfBirth ? getDateWithoutTZ(item.dateOfBirth, 'MM/dd/yyyy') : '-';
      },
    },
    {
      key: 'phoneNumber',
      label: t('table.phone_number'),
      sort: false,
      width: '16%',
      render: (_column: PaginatedTableColumn<T>, item: T) => {
        return item.phoneNumber ? maskPhoneNumber(item.phoneNumber) : '-';
      },
    },
    {
      key: 'patientID',
      label: t('table.patient_id'),
      sort: false,
    },
  ];

  useEffect(() => {
    setDataTable({
      rows: data?.data?.map((referral) => {
        return {
          ...referral,
        };
      }),
      error: isError,
      loading: isLoading || isFetching,
      offset,
      totalCount: data?.totalCount,
    });
  }, [data, isError, isFetching, isLoading, offset]);

  return {
    commonColumns,
    dataTable,
    pageSize,
    handleChangeSorting,
    handleChangePage,
    handleFilterChange,
    handleRowClick,
  };
};

export default useReferralsTable;
